"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { Typography } from "@repo/ui/aops-components/typography";
import { cn } from "@repo/ui/lib/utils";
import { splitKeyedCopyLink } from "@repo/utils/keyedCopy";
import { PanelCornerDots } from "@/app/components/PanelCornerDots";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import { trackDiscoveryLinkClick } from "@/lib/discoveryLinkTracking";
import { PROGRAM_END_CARD_CONTACT_HREF, PROGRAM_END_CARD_EXPLORER_LOCATION, PROGRAM_END_CARD_TRACKING_SOURCE, programExplorerCtaLocation, type ProgramEndCardLink } from "@/lib/programEndCard";
import styles from "./ProgramEndCard.module.css";
type ProgramEndCardProps = {
  title: string;
  body: string;
  helpText: string;
  links: ProgramEndCardLink[];
  useAltBackground?: boolean;
  /**
   * Page placement of this card. `cta_location` is `${trackingLocation}_program_explorer`
   * so the same card can be reused on other pages.
   */
  trackingLocation?: string;
};
export function ProgramEndCard(t0) {
  const $ = _c(50);
  const {
    title,
    body,
    helpText,
    links,
    useAltBackground: t1,
    trackingLocation: t2
  } = t0;
  const useAltBackground = t1 === undefined ? false : t1;
  const trackingLocation = t2 === undefined ? PROGRAM_END_CARD_EXPLORER_LOCATION : t2;
  if (links.length === 0) {
    return null;
  }
  let t3;
  if ($[0] !== helpText) {
    t3 = splitKeyedCopyLink(helpText);
    $[0] = helpText;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const help = t3;
  let ctaLocation;
  let t10;
  let t11;
  let t12;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== body || $[3] !== links || $[4] !== title || $[5] !== trackingLocation || $[6] !== useAltBackground) {
    ctaLocation = programExplorerCtaLocation(trackingLocation);
    const t13 = useAltBackground && styles.sectionAlt;
    if ($[17] !== t13) {
      t11 = cn(styles.section, t13);
      $[17] = t13;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    t12 = "program-end-card-title";
    t9 = styles.card;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <PanelCornerDots className={styles.dots} />;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    t7 = styles.content;
    let t14;
    if ($[20] !== title) {
      t14 = <Typography as="h2" variant="h3" id="program-end-card-title" className={styles.title}>{title}</Typography>;
      $[20] = title;
      $[21] = t14;
    } else {
      t14 = $[21];
    }
    let t15;
    if ($[22] !== body) {
      t15 = body && <Typography variant="body" as="p" className={styles.body}>{body}</Typography>;
      $[22] = body;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    if ($[24] !== t14 || $[25] !== t15) {
      t8 = <div className={styles.copy}>{t14}{t15}</div>;
      $[24] = t14;
      $[25] = t15;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    t4 = styles.ctas;
    t5 = title;
    t6 = links.map(link => <Link key={link.slug} href={link.href} className={styles.cta} onClick={() => sendDiscoveryEventBeacon("program_selected", {
      program_slug: link.slug,
      source: PROGRAM_END_CARD_TRACKING_SOURCE,
      cta_location: ctaLocation
    })}><Typography variant="buttonMd" as="span">{link.name}</Typography><span className={styles.ctaIcon} aria-hidden={true}><ArrowRight size={20} strokeWidth={2} /></span></Link>);
    $[2] = body;
    $[3] = links;
    $[4] = title;
    $[5] = trackingLocation;
    $[6] = useAltBackground;
    $[7] = ctaLocation;
    $[8] = t10;
    $[9] = t11;
    $[10] = t12;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    ctaLocation = $[7];
    t10 = $[8];
    t11 = $[9];
    t12 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t13;
  if ($[27] !== t4 || $[28] !== t5 || $[29] !== t6) {
    t13 = <nav className={t4} aria-label={t5}>{t6}</nav>;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== ctaLocation || $[32] !== help.after || $[33] !== help.before || $[34] !== help.label) {
    t14 = (help.before || help.label) && <Typography variant="bodySmall" as="p" className={styles.help}>{help.before}{help.label && <a href={PROGRAM_END_CARD_CONTACT_HREF} className={styles.helpLink} onClick={() => trackDiscoveryLinkClick(PROGRAM_END_CARD_CONTACT_HREF, ctaLocation)}>{help.label}</a>}{help.after}</Typography>;
    $[31] = ctaLocation;
    $[32] = help.after;
    $[33] = help.before;
    $[34] = help.label;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14 || $[38] !== t7 || $[39] !== t8) {
    t15 = <div className={t7}>{t8}{t13}{t14}</div>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t7;
    $[39] = t8;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <PanelCornerDots className={styles.dots} />;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t10 || $[43] !== t15 || $[44] !== t9) {
    t17 = <div className={t9}>{t10}{t15}{t16}</div>;
    $[42] = t10;
    $[43] = t15;
    $[44] = t9;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t11 || $[47] !== t12 || $[48] !== t17) {
    t18 = <section className={t11} aria-labelledby={t12}>{t17}</section>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  return t18;
}